import PropTypes from "prop-types";
import { Fragment, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import "../styles.css";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { getOEMParameters } from "@/Common/helpers/OEMs";

// Layouts components
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// MUI
import { Checkbox, Divider, FormControlLabel, Stack, Typography } from "@mui/material";

const getSelectAllCheckBoxState = (checkBoxes = []) => checkBoxes.every((checkBox) => checkBox?.checked);

const getModel = (args) => {
	const { variableGrossStructure, franchises, oems, objectRef, oemBased = false } = args || {};

	const checkBoxes = [];
	const makesUsed = [];
	let makesRemaining = [];

	if (objectRef === "newVehicles") {
		variableGrossStructure?.forEach((entry) => {
			checkBoxes.push(clone(entry));
			makesUsed.push(entry?.make);
		});

		if (oemBased) {
			makesRemaining = oems.filter((oem) => !makesUsed.includes(oem?.oemReference));
		} else {
			makesRemaining = franchises.filter((franchise) => !makesUsed.includes(franchise));
		}
		makesRemaining.forEach((make) => {
			checkBoxes.push({
				make,
				checked: false,
			});
		});

		checkBoxes.forEach((checkbox) => {
			let displayName = checkbox?.make;
			if (oemBased) {
				const oem = getOEMParameters({
					oemReference: checkbox?.make,
					dealerCodeFS: checkbox?.dealerCodeFS,
					locationOEMs: oems,
				});
				displayName = oem?.oemName || checkbox?.make;
			}
			checkbox["display"] = `New (${displayName})`;
		});
	} else if (objectRef === "finance") {
		const newFinance = variableGrossStructure?.newFinance || false;
		const preOwnedFinance = variableGrossStructure?.preOwnedFinance || false;

		checkBoxes.push({
			make: "newFinance",
			checked: newFinance,
			display: "New F&I",
		});
		checkBoxes.push({
			make: "preOwnedFinance",
			checked: preOwnedFinance,
			display: "Used F&I",
		});
	}

	return checkBoxes;
};

const RenderLabel = ({ label = "", classes = "", readOnly = false }) => {
	const defaultClasses = twMerge("!font-sans !text-[14.5px]", classes);

	return (
		<Typography
			className={clsx({
				[defaultClasses]: true,
				"!text-default": !readOnly,
				"!text-disabled": readOnly,
			})}
		>
			{label}
		</Typography>
	);
};
RenderLabel.propTypes = {
	label: PropTypes.string,
	classes: PropTypes.string,
	readOnly: PropTypes.bool,
};
export default function VariableGrossStructure(props) {
	const {
		oems = [],
		franchises = [],
		tabsErrors = [],
		variableGrossStructure = [],
		objectRef = "",
		oemBased = false,
		readOnly = false,
		showDivider = true,
		showTitle = true,
		onActionUpdate = () => {},
		onActionValidation = () => {},
		validateAgainst0 = true,
	} = props;

	const wasUpdated = useRef(false);

	const [isValid, setIsValid] = useState(false);

	const onVariableGrossStructureUpdate = (updatedVGS) => {
		const isValidModel = validateVariableGrossStructureModel(updatedVGS);
		setIsValid(isValidModel);

		onActionValidation?.("variableGrossStructure", isValidModel);
		if (wasUpdated.current) {
			onActionUpdate?.("variableGrossStructure", getVariableGrossStructureFromModel(updatedVGS), isValidModel);
		}
	};

	const [checkBoxes, setCheckBoxes] = useCallbackState([], onVariableGrossStructureUpdate);

	const getVariableGrossStructureFromModel = (model) => {
		const grossStructure = clone(model);
		grossStructure.forEach((checkBox) => {
			delete checkBox?.display;
		});
		if (objectRef === "finance") {
			return {
				newFinance: model?.[0]?.checked,
				preOwnedFinance: model?.[1]?.checked,
			};
		}
		return grossStructure;
	};

	const validateVariableGrossStructureModel = (localCheckBoxes = []) => {
		if (objectRef === "finance") {
			return true;
		}

		if (Array.isArray(localCheckBoxes)) {
			if (validateAgainst0) {
				return localCheckBoxes.some((checkBox) => checkBox?.checked);
			}
			return true;
		}

		return false;
	};

	useEffect(() => {
		const checkBoxesModel = getModel({ variableGrossStructure, franchises, oems, objectRef, oemBased });
		setCheckBoxes(checkBoxesModel);

		const isValidModel = validateVariableGrossStructureModel(variableGrossStructure);
		setIsValid(isValidModel);
		onActionValidation?.("variableGrossStructure", isValidModel);

		return () => {
			wasUpdated.current = false;
			setCheckBoxes([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variableGrossStructure, franchises, objectRef, setCheckBoxes]);

	const handleChangeCheck = (index, value) => {
		checkBoxes[index].checked = value;
		update(clone(checkBoxes));
	};

	const handleSelectAll = (value) => {
		checkBoxes.forEach((checkBox) => {
			checkBox.checked = value;
		});
		update(clone(checkBoxes));
	};

	const update = (updatedCheckBoxes) => {
		wasUpdated.current = true;
		setCheckBoxes(updatedCheckBoxes);
	};

	return (
		<Stack
			width={1}
			style={{
				paddingTop: "10px",
			}}
		>
			{Boolean(showDivider) && (
				<Stack mt={1.5}>
					<Divider
						className="!border-disabled"
						sx={{
							paddingTop: "10px",
							marginBottom: "16px",
						}}
					/>
				</Stack>
			)}
			{Boolean(showTitle) && (
				<Typography className="!font-montserrat !font-[500] !text-default !text-[20px]">Variable Gross Structure</Typography>
			)}
			<Stack mt={1} ml={-1.3}>
				<Stack direction="row" spacing={2}>
					<FormControlLabel
						className={clsx({
							"checkbox-error": false,
						})}
						control={
							<Checkbox
								checked={getSelectAllCheckBoxState(checkBoxes)}
								disabled={readOnly}
								onChange={() => handleSelectAll(!getSelectAllCheckBoxState(checkBoxes))}
							/>
						}
						label={<RenderLabel label={"Select All"} readOnly={readOnly} />}
						labelPlacement="end"
						sx={{ fontSize: "14.5 !important" }}
					/>
				</Stack>
				<Stack direction="row" spacing={2} mt={1}>
					{checkBoxes.map((checkBox, key) => {
						return (
							<Fragment key={`${objectRef}_${key}`}>
								<FormControlLabel
									className={clsx({
										"checkbox-error": false,
									})}
									control={
										<Checkbox
											checked={Boolean(checkBox?.checked)}
											disabled={readOnly}
											onChange={(_, value) => handleChangeCheck(key, value)}
										/>
									}
									label={<RenderLabel label={checkBox?.display} readOnly={readOnly} />}
									labelPlacement="end"
									sx={{ fontSize: "14.5 !important" }}
								/>
							</Fragment>
						);
					})}
				</Stack>
				<Stack>{Boolean(!isValid) && <TierAlertError text={"Must select at least one franchise."} />}</Stack>
			</Stack>
		</Stack>
	);
}
VariableGrossStructure.propTypes = {
	oems: PropTypes.array,
	franchises: PropTypes.array,
	tabsErrors: PropTypes.array,
	variableGrossStructure: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	objectRef: PropTypes.string,
	oemBased: PropTypes.bool,
	readOnly: PropTypes.bool,
	showDivider: PropTypes.bool,
	showTitle: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
